import React, { useRef, useState } from 'react';
import { withStyles } from 'tss-react/mui';
import { Grid, Button, Box, Typography } from '@mui/material';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';

const styles = {
  container: {
    border: '1px solid rgba(0, 0, 0, 0.23)',
    width: '100%',
    padding: '20px',
    margin: '8px',
    borderRadius: '4px',
    position: 'relative'
  },
  label: {
    marginLeft: '10px',
    padding: '4px',
    fontSize: '1rem',
    color: 'rgba(0, 0, 0, 0.54)',
    backgroundColor: 'white',
    position: 'absolute',
    top: '-8px',
    left: '-10px',
    transform: 'translate(14px, -6px) scale(0.75)'
  },
  error: {
    textAlign: 'center',
    color: 'red',
    fontSize: '0.875rem',
    marginTop: '8px'
  },
  fileInput: {
    display: 'block',
    marginBottom: '10px'
  },
  cropperContainer: {
    width: '100%',
    height: '300px'
  },
  previewImage: {
    maxWidth: '200px',
    maxHeight: '200px',
    marginRight: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px'
  },
  dropZone: {
    border: '2px dashed #ccc',
    borderRadius: '8px',
    padding: '40px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginBottom: '10px',
    '&:hover': {
      borderColor: '#1976d2',
      backgroundColor: 'rgba(25, 118, 210, 0.04)'
    }
  },
  dropZoneActive: {
    borderColor: '#1976d2',
    backgroundColor: 'rgba(25, 118, 210, 0.08)',
    '& $dropZoneText': {
      color: '#1976d2'
    }
  },
  dropZoneText: {
    color: '#666',
    fontSize: '1rem',
    marginBottom: '8px'
  },
  dropZoneSubtext: {
    color: '#999',
    fontSize: '0.875rem'
  },
  previewContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px'
  },
  previewRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px'
  },
  previewLabel: {
    fontSize: '0.875rem',
    fontWeight: 'bold',
    color: 'rgba(0, 0, 0, 0.87)',
    textAlign: 'center'
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '16px'
  },
  saveButton: {
    backgroundColor: '#00adb5',
    color: '#FFFFFF',
    '&:hover': {
      boxShadow:
        'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px',
      backgroundColor: '#282c34'
    }
  },
  cancelButton: {
    backgroundColor: '#FF0000',
    color: '#FFFFFF',
    '&:hover': {
      boxShadow:
        '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
      backgroundColor: '#FF0000'
    }
  }
};

const ImageUploadWithCrop = ({
  label,
  name,
  value,
  onChange,
  onGifChange,
  classes
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(value || null);
  const [gifPreview, setGifPreview] = useState(null);
  const [fallbackImageSrc, setFallbackImageSrc] = useState(null);
  const [error, setError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isFallbackDragOver, setIsFallbackDragOver] = useState(false);
  const [isGif, setIsGif] = useState(false);
  const [isUploadingFallback, setIsUploadingFallback] = useState(false);
  const cropperRef = useRef(null);
  const fileInputRef = useRef(null);
  const fallbackInputRef = useRef(null);

  const validateAndProcessFile = (file, isFallbackUpload = false) => {
    // Reset error state
    setError('');

    if (file.size > 5 * 1000 * 1000) {
      setError('File size exceeds 5MB limit. Please upload a smaller image.');
      if (fileInputRef.current && !isFallbackUpload) {
        fileInputRef.current.value = '';
      }
      if (fallbackInputRef.current && isFallbackUpload) {
        fallbackInputRef.current.value = '';
      }
      return;
    }

    const isGifFile = file.type === 'image/gif' && !isFallbackUpload;

    // GIFs are not allowed as fallback images
    if (isFallbackUpload && file.type === 'image/gif') {
      setError(
        'Fallback image cannot be a GIF. Please select a JPG or PNG file.'
      );
      if (fallbackInputRef.current) {
        fallbackInputRef.current.value = '';
      }
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const { width, height } = img;
      if (width < 622 || height < 350) {
        setError(
          'Image dimensions must be at least 622x350 pixels to support a 16:9 aspect ratio.'
        );
        if (!isFallbackUpload) {
          setImageSrc(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } else {
          // For fallback images, only clear fallback-related state
          setFallbackImageSrc(null);
          if (fallbackInputRef.current) {
            fallbackInputRef.current.value = '';
          }
        }
        URL.revokeObjectURL(img.src);
        return;
      }

      setError('');

      if (isFallbackUpload) {
        // Handle fallback image for GIF
        setIsUploadingFallback(true);
        setFallbackImageSrc(URL.createObjectURL(file));
      } else if (isGifFile) {
        // Handle GIF upload
        setIsGif(true);
        setGifPreview(URL.createObjectURL(file));
        setImageSrc(null);
        setCroppedImage(null);
        // Trigger GIF change callback
        if (onGifChange) {
          onGifChange(name, file);
        }
      } else {
        // Handle regular image upload
        setIsGif(false);
        setImageSrc(URL.createObjectURL(file));
        setCroppedImage(null);
        setGifPreview(null);
      }
    };

    img.onerror = () => {
      setError('Failed to load the image. Please try a different file.');
      if (!isFallbackUpload) {
        setImageSrc(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        // For fallback images, only clear fallback-related state
        setFallbackImageSrc(null);
        if (fallbackInputRef.current) {
          fallbackInputRef.current.value = '';
        }
      }
      URL.revokeObjectURL(img.src);
    };
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndProcessFile(file, false);
    }
  };

  const handleFallbackFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndProcessFile(file, true);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find((file) => file.type.startsWith('image/'));

    if (imageFile) {
      validateAndProcessFile(imageFile, false);
    } else if (files.length > 0) {
      setError('Please drop an image file.');
    }
  };

  const handleFallbackDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFallbackDragOver(true);
  };

  const handleFallbackDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFallbackDragOver(false);
  };

  const handleFallbackDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFallbackDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFallbackDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find((file) => file.type.startsWith('image/'));

    if (imageFile) {
      validateAndProcessFile(imageFile, true);
    } else if (files.length > 0) {
      setError('Please drop an image file.');
    }
  };

  const handleDropZoneClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFallbackUploadClick = () => {
    if (fallbackInputRef.current) {
      fallbackInputRef.current.click();
    }
  };

  const handleCropSave = () => {
    if (cropperRef.current && cropperRef.current.cropper) {
      const croppedCanvas = cropperRef.current.cropper.getCroppedCanvas();
      if (croppedCanvas.width < 622 || croppedCanvas.height < 350) {
        setError(
          'Cropped image must be at least 400x350 pixels (minimum 622x350 pixels to maintain a 16:9 aspect ratio).'
        );
        setImageSrc(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      croppedCanvas.toBlob((blob) => {
        const file = new File([blob], 'cropped-image.jpg', {
          type: 'image/jpeg',
          lastModified: Date.now()
        });

        if (isUploadingFallback) {
          // This is a fallback image being cropped
          setCroppedImage(URL.createObjectURL(file));
          setFallbackImageSrc(null);
          setIsUploadingFallback(false);
          onChange(name, file);
        } else {
          // This is a regular image being cropped
          setCroppedImage(URL.createObjectURL(file));
          setImageSrc(null);
          onChange(name, file);
        }

        setError('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        if (fallbackInputRef.current) {
          fallbackInputRef.current.value = '';
        }
      }, 'image/jpeg');
    }
  };

  const handleRemove = () => {
    setImageSrc(null);
    setCroppedImage(null);
    setGifPreview(null);
    setFallbackImageSrc(null);
    setError('');
    setIsGif(false);
    setIsUploadingFallback(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (fallbackInputRef.current) {
      fallbackInputRef.current.value = '';
    }
    onChange(name, null);
    if (onGifChange) {
      onGifChange(name, null);
    }
  };

  const handleRemoveFallback = () => {
    setImageSrc(null);
    setCroppedImage(null);
    setFallbackImageSrc(null);
    setError('');
    setIsUploadingFallback(false);
    if (fallbackInputRef.current) {
      fallbackInputRef.current.value = '';
    }
    onChange(name, null);
  };

  const canCompleteGifUpload = isGif && gifPreview && croppedImage;

  return (
    <Grid container alignItems="center" justifyContent="left">
      <div className={classes.container}>
        <div className={classes.label}>{label}</div>

        {!imageSrc && !croppedImage && !gifPreview && !fallbackImageSrc && (
          <Box>
            <div
              className={`${classes.dropZone} ${
                isDragOver ? classes.dropZoneActive : ''
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleDropZoneClick}
            >
              <Typography className={classes.dropZoneText}>
                Drag & drop an image here, or click to select
              </Typography>
              <Typography className={classes.dropZoneSubtext}>
                Supports JPG, PNG, GIF (max 5MB, min 622x350px)
              </Typography>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
          </Box>
        )}

        {/* GIF uploaded, need fallback image */}
        {isGif &&
          gifPreview &&
          !croppedImage &&
          !fallbackImageSrc &&
          !isUploadingFallback && (
            <Box>
              <div className={classes.previewContainer}>
                <div className={classes.previewRow}>
                  <Typography className={classes.previewLabel}>GIF:</Typography>
                  <img
                    src={gifPreview}
                    alt="GIF Preview"
                    className={classes.previewImage}
                  />
                </div>
              </div>

              <Typography
                variant="body2"
                style={{
                  margin: '16px 0 8px 0',
                  color: 'rgba(0, 0, 0, 0.87)',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}
              >
                Please upload a fallback image (JPG/PNG) for the GIF:
              </Typography>

              <div
                className={`${classes.dropZone} ${
                  isFallbackDragOver ? classes.dropZoneActive : ''
                }`}
                onDragEnter={handleFallbackDragEnter}
                onDragLeave={handleFallbackDragLeave}
                onDragOver={handleFallbackDragOver}
                onDrop={handleFallbackDrop}
                onClick={handleFallbackUploadClick}
              >
                <Typography className={classes.dropZoneText}>
                  Drag & drop fallback image here, or click to select
                </Typography>
                <Typography className={classes.dropZoneSubtext}>
                  JPG or PNG only (max 5MB, min 622x350px)
                </Typography>
              </div>

              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleFallbackFileChange}
                ref={fallbackInputRef}
                style={{ display: 'none' }}
              />

              <Box mt={2} className={classes.buttonRow}>
                <Button
                  variant="contained"
                  className={classes.cancelButton}
                  onClick={handleRemove}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}

        {(imageSrc || fallbackImageSrc) && (
          <Box>
            <div className={classes.cropperContainer}>
              <Cropper
                ref={cropperRef}
                src={imageSrc || fallbackImageSrc}
                style={{ height: '100%', width: '100%' }}
                guides
                viewMode={1}
                minCropBoxWidth={622}
                minCropBoxHeight={350}
                aspectRatio={16 / 9}
                background={false}
                responsive
                autoCropArea={1}
                checkOrientation={false}
              />
            </div>
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button
                variant="contained"
                className={classes.cancelButton}
                onClick={fallbackImageSrc ? handleRemoveFallback : handleRemove}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={classes.saveButton}
                onClick={handleCropSave}
              >
                Save
              </Button>
            </Box>
          </Box>
        )}

        {(canCompleteGifUpload || (croppedImage && !isGif)) && (
          <Box mt={2}>
            <div className={classes.previewContainer}>
              {isGif && gifPreview && (
                <div className={classes.previewRow}>
                  <Typography className={classes.previewLabel}>GIF:</Typography>
                  <img
                    src={gifPreview}
                    alt="GIF Preview"
                    className={classes.previewImage}
                  />
                </div>
              )}

              <div className={classes.previewRow}>
                {isGif && (
                  <Typography className={classes.previewLabel}>
                    Fallback Image:
                  </Typography>
                )}
                <img
                  src={croppedImage}
                  alt={isGif ? 'Fallback Preview' : 'Image Preview'}
                  className={classes.previewImage}
                />
              </div>
            </div>
            <Box className={classes.buttonRow}>
              <Button
                variant="contained"
                className={classes.cancelButton}
                onClick={handleRemove}
              >
                {isGif ? 'Remove GIF and Fallback Image' : 'Remove Image'}
              </Button>
            </Box>
          </Box>
        )}

        {error && <div className={classes.error}>{error}</div>}
      </div>
    </Grid>
  );
};

export default withStyles(ImageUploadWithCrop, styles);
